import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import "../styles/SignUpModal.css";
import CropperModal from "./CropperModal";

const SIGNUP_URL =
  process.env.NODE_ENV === "development"
    ? "http://localhost:4567/api/signup"
    : "https://health-ai-zfr7.onrender.com/api/signup";

function dataURLtoFile(dataUrl, filename = "profile.jpg") {
  const arr = dataUrl.split(",");
  const mime = arr[0].match(/:(.*?);/)[1] || "image/jpeg";
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) u8arr[n] = bstr.charCodeAt(n);
  return new File([u8arr], filename, { type: mime });
}

export default function SignUpModal({ onClose, onSignupSuccess }) {
  const { t } = useTranslation();
  const fileRef = useRef(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    dob: "",
    height: "",
    weight: "",
    gender: "Male",
    horoscope: "",
  });
  const [rawImage, setRawImage] = useState(null);
  const [showCropper, setShowCropper] = useState(false);
  const [croppedBase64, setCroppedBase64] = useState("");

  const previewSrc = croppedBase64 || rawImage || "";

  const onPickFile = () => fileRef.current?.click();

  const handleFileChange = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    const reader = new FileReader();
    reader.onload = () => {
      setRawImage(reader.result);
      setShowCropper(true);
    };
    reader.readAsDataURL(f);
  };

  const handleCropComplete = (base64) => setCroppedBase64(base64);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const setGender = (g) => setForm((p) => ({ ...p, gender: g }));

  const handleSubmit = async () => {
    setErr("");
    if (!form.name.trim()) return setErr("Full name is required.");
    if (!form.email.trim()) return setErr("Email is required.");
    if (!form.password || form.password.length < 6)
      return setErr("Password (min 6) is required.");
    if (!form.dob) return setErr("Date of Birth is required.");
    if (!form.height || Number(form.height) <= 0)
      return setErr("Height (cm) is required.");
    if (!form.weight || Number(form.weight) <= 0)
      return setErr("Weight (kg) is required.");
    if (!croppedBase64 && !rawImage)
      return setErr("Please choose and crop a profile picture.");

    const fd = new FormData();
    fd.append("name", form.name);
    fd.append("email", form.email);
    fd.append("password", form.password);
    fd.append("dob", form.dob);
    fd.append("height", form.height);
    fd.append("weight", form.weight);
    fd.append("gender", form.gender);
    fd.append("horoscope", form.horoscope); // ✅ NEW
    fd.append("profilePic", dataURLtoFile(croppedBase64 || rawImage));

    try {
      setLoading(true);
      const r = await fetch(SIGNUP_URL, { method: "POST", body: fd });
      const j = await r.json();
      if (!r.ok || j?.status !== "success") {
        setErr(j?.message || "Signup failed.");
        return;
      }
      const u = j.user || {};
      localStorage.setItem("isLoggedIn", "true");
      localStorage.setItem("userName", u.name || "");
      localStorage.setItem("userEmail", u.email || "");
      localStorage.setItem("userPic", u.profilePic || "");
      localStorage.setItem("userDOB", u.dob || "");
      localStorage.setItem("userHeight", u.height || "");
      localStorage.setItem("userWeight", u.weight || "");
      localStorage.setItem("userGender", u.gender || "");
      localStorage.setItem("userHoroscope", u.horoscope || "");
      onSignupSuccess?.(u);
      onClose?.();
    } catch {
      setErr("Could not reach the server.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="signup-overlay su-light" role="dialog" aria-modal="true">
      <div className="signup-popup">
        <div className="signup-header">
          <h2>{t('sign_up')}</h2>
          <button className="xbtn" onClick={onClose} aria-label={t('close')}>×</button>
        </div>

        <div className="signup-grid">
          <div className="signup-left">
            <label className="lbl">{t('full_name')}</label>
            <input name="name" className="modal-input" value={form.name} onChange={handleChange} placeholder={t('full_name')} required />

            <label className="lbl">{t('email')}</label>
            <input name="email" type="email" className="modal-input" value={form.email} onChange={handleChange} placeholder={t('email')} required />

            <label className="lbl">{t('password')}</label>
            <input name="password" type="password" className="modal-input" value={form.password} onChange={handleChange} placeholder={t('password')} required />

            <label className="lbl">{t('dob')}</label>
            <input name="dob" type="date" className="modal-input" value={form.dob} onChange={handleChange} required />

            <div className="row-2">
              <div>
                <label className="lbl">{t('height_cm')}</label>
                <input name="height" type="number" className="modal-input" value={form.height} onChange={handleChange} min="1" />
              </div>
              <div>
                <label className="lbl">{t('weight_kg')}</label>
                <input name="weight" type="number" className="modal-input" value={form.weight} onChange={handleChange} min="1" step="0.1" />
              </div>
            </div>

            <label className="lbl">{t('gender')}</label>
            <div className="seg-group">
              {['Male', 'Female', 'Other'].map((g) => (
                <label key={g} className={`seg ${form.gender === g ? "seg-active" : ""}`} onClick={() => setGender(g)}>
                  <input type="radio" name="gender" value={g} checked={form.gender === g} onChange={() => setGender(g)} />{t(g.toLowerCase())}
                </label>
                
              ))}
            </div>

            <label className="lbl">{t('horoscope')}</label>
<select
  name="horoscope"
  className="modal-input"
  value={form.horoscope}
  onChange={handleChange}
  required
>
  <option value="">{t('select_zodiac')}</option>
  <option value="Aries">Aries</option>
  <option value="Taurus">Taurus</option>
  <option value="Gemini">Gemini</option>
  <option value="Cancer">Cancer</option>
  <option value="Leo">Leo</option>
  <option value="Virgo">Virgo</option>
  <option value="Libra">Libra</option>
  <option value="Scorpio">Scorpio</option>
  <option value="Sagittarius">Sagittarius</option>
  <option value="Capricorn">Capricorn</option>
  <option value="Aquarius">Aquarius</option>
  <option value="Pisces">Pisces</option>
</select>

            <div className="uploader">
              <button className="btn-outline" type="button" onClick={onPickFile}>{t('choose_profile_picture')}</button>
              <input ref={fileRef} type="file" accept="image/*" onChange={handleFileChange} hidden />
              {rawImage && !croppedBase64 && <span className="muted">(crop to apply)</span>}
            </div>

            {err && <p className="warn">{err}</p>}

            <div className="actions">
              <button className="btn-primary" onClick={handleSubmit} disabled={loading}>
                {loading ? t('creating') : t('create_account')}
              </button>
              <button className="btn-ghost" onClick={onClose} disabled={loading}>{t('close')}</button>
            </div>
          </div>

          <aside className="signup-right">
            <div className="avatar-wrap">
              {previewSrc ? <img className="avatar" src={previewSrc} alt="Preview avatar" /> : <div className="avatar placeholder">☺</div>}
            </div>
            <div className="preview-meta">
              <div><strong>{t('name')}:</strong> <span>{form.name || "—"}</span></div>
              <div><strong>{t('gender')}:</strong> <span>{t(form.gender.toLowerCase()) || "—"}</span></div>
              <div><strong>{t('height')}:</strong> <span>{form.height ? `${form.height} cm` : "—"}</span></div>
              <div><strong>{t('weight')}:</strong> <span>{form.weight ? `${form.weight} kg` : "—"}</span></div>
              <div className="mini-hint">{t('crop_tip')}</div>
            </div>
          </aside>
        </div>

        <div className="signup-footnote">{t('all_fields_required')}</div>
      </div>

      {showCropper && rawImage && (
        <CropperModal
          image={rawImage}
          onClose={() => setShowCropper(false)}
          onCropComplete={(b64) => {
            handleCropComplete(b64);
            setShowCropper(false);
          }}
        />
      )}
</div>
  );
}